import * as React from 'react'

import { useApolloClient, useQuery } from '@apollo/client'
import { Avatar, Button } from '@factorialco/deprecated-design-system'
import {
  EmployeeForLocationDocument,
  LocationsEmployeesCollectionListDocument,
  UpdateEmployeeWithLocationDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import SelectField from 'modules/core/components/SelectField'
import { useMemo } from 'modules/core/hooks/useMemo'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useLocations } from 'modules/locations/hooks/useLocations'

interface Props {
  locationId: string
  employeeId: string
  onClose: () => void
}

const MoveEmployee = ({ employeeId, locationId, onClose }: Props) => {
  const [newLocationId, setNewLocationId] = React.useState<string | null>(null)
  const { loading: employeeLoading, data: employeeData } = useQuery(EmployeeForLocationDocument, {
    variables: { employeeId: employeeId },
    fetchPolicy: 'network-only',
  })
  const { loading: locationsLoading, locations } = useLocations()

  const apolloClient = useApolloClient()

  const options = useMemo(() => {
    return locations
      .filter((location) => location.id !== locationId)
      .map((location) => ({
        label: location.name,
        value: location.id,
      }))
  }, [locationId, locationsLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSelect = (value: string) => {
    setNewLocationId(value)
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!newLocationId) return

    await apolloClient.mutate({
      mutation: UpdateEmployeeWithLocationDocument,
      variables: {
        employeeId: employeeId,
        locationId: newLocationId,
      },
      refetchQueries: [LocationsEmployeesCollectionListDocument],
    })

    closePortal()
  }

  if (employeeLoading || locationsLoading || !employeeData) return null

  const employee = employeeData.employees.employee

  if (!employee) return null

  return (
    <Modal
      illustration={
        <Avatar label={employee.fullName} url={buildUrl(employee.avatar?.url)} size="s32" />
      }
      title={i18n.t('settings.locations.move_employee_modal.title', {
        employee: employee.fullName,
      })}
      description={i18n.t('settings.locations.move_employee_modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={!newLocationId} submit>
          {i18n.t('common.submit')}
        </Button>
      }
    >
      <SelectField
        none={i18n.t('common.none')}
        value={newLocationId?.toString()}
        label={i18n.t('settings.locations.title')}
        onChange={(value) => onSelect(value)}
        options={options}
      />
    </Modal>
  )
}

export default MoveEmployee
